import React, { useEffect, useState, useCallback } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { Box, Container, Divider } from "@material-ui/core";
import { useBmapi } from "../../utils/bmapi-context";
import { campaign as campaignMessages, common } from "../../messages";
import Title from "../../ui/Title";
import FormDialog from "../../ui/FormDialog";
import ImageManagerDialog from "../../components/ImageManagerDialog";
import Notification from "./Notification";
import { initCreator } from "../../utils/notifications";
import { getErrorMessageString } from "../../utils/errors";
import NotificationCard from "../../components/NotificationCard";

export default function CampaignNotifications() {
  const { campaignId: campaignIdParam } = useParams();
  const isTemplateMode = !campaignIdParam;
  const effectiveCampaignId = campaignIdParam ?? "DEFAULT";

  const intl = useIntl();
  const {
    bmapi,
    startLoading,
    stopLoading,
    notifySuccess,
    notifyError,
  } = useBmapi();

  const [campaign, setCampaign] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [values, setValues] = useState([]);
  const [creator, setCreator] = useState({ ...initCreator });
  const [report, setReport] = useState({ total: 0, active: 0 });
  const openModal = React.useRef(null);
  const [consents, setConsents] = useState(null);
  const [imagesPath, setImagesPath] = useState(null);
  const [notifConfig, setNotifConfig] = useState(null);

  // --- helpers di ordinamento (usa notifConfig *corrente*, senza entrare nelle deps)
  const sortNotificationsByConfig = React.useCallback(
    (arr, cfg = notifConfig) => {
      if (!Array.isArray(arr)) return [];
      const orderMap = new Map();
      if (Array.isArray(cfg?.events)) {
        cfg.events.forEach((ev, idx) => orderMap.set(ev.type, idx));
      }
      const idxOf = (t) =>
        orderMap.has(t) ? orderMap.get(t) : Number.MAX_SAFE_INTEGER;
      const daysOf = (x) =>
        x?.remind_days != null && !isNaN(parseInt(x.remind_days, 10))
          ? parseInt(x.remind_days, 10)
          : 0;

      const copy = arr.slice();
      copy.sort((a, b) => {
        const ai = idxOf(a?.event_type);
        const bi = idxOf(b?.event_type);
        if (ai !== bi) return ai - bi;
        return daysOf(a) - daysOf(b);
      });
      return copy;
    },
    [notifConfig]
  );

  // confronto superficiale per evitare setState inutili
  const sameOrder = (a, b) => {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length)
      return false;
    for (let i = 0; i < a.length; i++) {
      // confronta la chiave d’ordinamento principale e secondaria
      const ax = a[i];
      const bx = b[i];
      const aDays =
        ax?.remind_days != null && !isNaN(parseInt(ax.remind_days, 10))
          ? parseInt(ax.remind_days, 10)
          : 0;
      const bDays =
        bx?.remind_days != null && !isNaN(parseInt(bx.remind_days, 10))
          ? parseInt(bx.remind_days, 10)
          : 0;
      if (
        ax?.event_type !== bx?.event_type ||
        aDays !== bDays ||
        ax?.id !== bx?.id
      ) {
        return false;
      }
    }
    return true;
  };

  // fetch notifiche (NON dipende dalla config, così resta stabile)
  const fetchNotifications = useCallback(() => {
    return bmapi.getCampaignNotifications(effectiveCampaignId).then((vals) => {
      const incoming = Array.isArray(vals) ? vals.slice() : [];
      incoming.forEach((element) => {
        element.show = false;
        element.title = element.event_type;
      });
      // ordino con la config *corrente* (non in deps → niente loop)
      const sorted = sortNotificationsByConfig(incoming, notifConfig);
      setValues(sorted);
      setNotifications(sorted);
    });
  }, [bmapi, effectiveCampaignId, sortNotificationsByConfig, notifConfig]);

  const handleSubmit = (position, event_type, after) => {
    startLoading();
    if (position === -1) {
      const data = { ...creator, event_type };
      return saveNotificationCampaign(data, after);
    } else {
      const data = notifications[position];
      return updateNotificationCampaign(data, after);
    }
  };

  const handleSubmitWithDialog = () => {
    if (openModal.current) openModal.current();
  };

  const updateNotificationCampaign = (data, after) => {
    return bmapi
      .updateNotificationCampaign(
        data.event_type,
        data.active,
        data.in_app_active,
        data.in_app_title,
        data.in_app_body,
        data.banner_active,
        data.banner_title,
        data.banner_body,
        data.push_active,
        data.push_title,
        data.push_body,
        data.push_image_url,
        data.email_active,
        data.email_subject,
        data.email_body,
        data.email_from,
        data.email_from_name,
        data.remind_days ? parseInt(data.remind_days) : 0,
        effectiveCampaignId,
        data.id,
        data.consent_id
      )
      .then(after)
      .then(fetchNotifications)
      .then(() => {
        notifySuccess(intl.formatMessage(campaignMessages.createdNotification));
        setCreator({ ...initCreator });
      })
      .catch((e) => {
        notifyError(getErrorMessageString(e, intl));
      })
      .finally(stopLoading);
  };

  const saveNotificationCampaign = (data, after) => {
    return bmapi
      .saveNotificationCampaign(
        data.event_type,
        data.active,
        data.in_app_active,
        data.in_app_title,
        data.in_app_body,
        data.banner_active,
        data.banner_title,
        data.banner_body,
        data.push_active,
        data.push_title,
        data.push_body,
        data.push_image_url,
        data.email_active,
        data.email_subject,
        data.email_body,
        data.email_from,
        data.email_from_name,
        data.remind_days ? parseInt(data.remind_days) : 0,
        effectiveCampaignId,
        data.consent_id
      )
      .then(after)
      .then(fetchNotifications)
      .then(() => {
        notifySuccess(intl.formatMessage(campaignMessages.createdNotification));
        setCreator({ ...initCreator });
      })
      .catch((e) => {
        notifyError(getErrorMessageString(e, intl));
      })
      .finally(stopLoading);
  };

  const handleChange = (notification, key, position) => {
    const updateNotification = (val) => {
      if (position === -1) {
        setCreator((prev) => ({ ...prev, [key]: val }));
      } else {
        setNotifications((prev) => {
          const next = prev.slice();
          next[position] = { ...next[position], [key]: val };
          return next;
        });
      }
    };
    return (i, f) => {
      if (typeof f === "boolean") {
        updateNotification(f);
      } else if (i?.target) {
        updateNotification(i.target.value);
      } else {
        updateNotification(i);
      }
    };
  };

  const handleDelete = (id) => {
    startLoading();
    return bmapi
      .deleteNotificationCampaign(effectiveCampaignId, id)
      .then(fetchNotifications)
      .then(() => {
        notifySuccess(intl.formatMessage(common.deleteElement));
      })
      .catch((e) => {
        notifyError(getErrorMessageString(e, intl));
      })
      .finally(stopLoading);
  };

  // FIRST LOAD (non dipende da fetchNotifications per evitare rimbalzi)
  useEffect(() => {
    startLoading();

    const campaignPromise = !isTemplateMode
      ? bmapi.getCampaign(effectiveCampaignId).then(({ campaign }) => {
          setCampaign(campaign);
        })
      : Promise.resolve(
          setCampaign({ id: "DEFAULT", name: "Template notifiche" })
        );

    const notificationsPromise = bmapi
      .getCampaignNotifications(effectiveCampaignId)
      .then((vals) => {
        const incoming = Array.isArray(vals) ? vals.slice() : [];
        incoming.forEach((element) => {
          element.show = false;
          element.title = element.event_type;
        });
        const sorted = sortNotificationsByConfig(incoming, notifConfig);
        setValues(sorted);
        setNotifications(sorted);
      });

    const configAndConsentsPromise = bmapi
      .getCampaignNotificationsConfig()
      .then((nc) => {
        setNotifConfig(nc);
        const restrictFromConfig = Array.isArray(nc?.consent_types)
          ? nc.consent_types
          : null;

        return bmapi.getConsent().then((resp) => {
          const apiList = Array.isArray(resp)
            ? resp
            : Array.isArray(resp?.consents)
            ? resp.consents
            : [];

          const restrictFromResp = Array.isArray(
            resp?.notif_config?.consent_types
          )
            ? resp.notif_config.consent_types
            : null;

          const restrict = restrictFromConfig || restrictFromResp || [];

          const filtered =
            Array.isArray(restrict) && restrict.length > 0
              ? apiList.filter((c) => restrict.includes(c?.type))
              : apiList;

          const hasEmptyFromApi = filtered.some((c) => (c?.id ?? "") === "");

          const shouldAddEmpty =
            !(Array.isArray(restrict) && restrict.length > 0) ||
            restrict.includes("");

          const withOptionalEmpty =
            shouldAddEmpty && !hasEmptyFromApi
              ? [
                  { id: "", title: "Consenso non necessario", type: "" },
                  ...filtered,
                ]
              : filtered;

          setConsents(withOptionalEmpty);
        });
      })
      .catch((e) => notifyError(getErrorMessageString(e, intl)));

    const imagesPromise = bmapi
      .getNotificationImagesPath()
      .then((l) => setImagesPath(l?.dir_url))
      .catch((e) => notifyError(getErrorMessageString(e, intl)));

    Promise.all([
      campaignPromise,
      notificationsPromise,
      configAndConsentsPromise,
      imagesPromise,
    ])
      .catch((e) => {
        notifyError(getErrorMessageString(e, intl));
      })
      .finally(stopLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bmapi,
    effectiveCampaignId,
    intl,
    isTemplateMode,
    notifyError,
    startLoading,
    stopLoading,
  ]);

  // Quando cambia la config, riordina *solo se* l’ordine risultante è diverso
  useEffect(() => {
    if (!Array.isArray(notifConfig?.events)) return;
    setNotifications((prev) => {
      const sorted = sortNotificationsByConfig(prev, notifConfig);
      return sameOrder(prev, sorted) ? prev : sorted;
    });
    setValues((prev) => {
      const sorted = sortNotificationsByConfig(prev, notifConfig);
      return sameOrder(prev, sorted) ? prev : sorted;
    });
  }, [notifConfig, sortNotificationsByConfig]);

  useEffect(() => {
    setReport({
      total: notifications.length,
      active: notifications.filter((x) => x.active).length,
    });
  }, [notifications]);

  const renderedResults = notifications.map((n, index) => (
    <div key={n.id || `${n.event_type}-${index}`} className="item">
      <NotificationCard
        value={n}
        onChange={(key, val) =>
          setNotifications((prev) => {
            const next = prev.slice();
            next[index] = { ...next[index], [key]: val };
            return next;
          })
        }
        onSubmit={() => handleSubmit(index)}
        onToggleExpand={() =>
          setNotifications((prev) => {
            const next = prev.slice();
            next[index] = { ...next[index], show: !next[index].show };
            return next;
          })
        }
        onDelete={() => handleDelete(n.id)}
        consents={consents}
        imagesPath={imagesPath}
        notifConfig={notifConfig}
      />
    </div>
  ));

  return (
    campaign && (
      <React.Fragment>
        <Container maxWidth="sm">
          <Box display="flex" alignItems="center" mb={1}>
            <Box flexGrow={1}>
              <Title>
                {isTemplateMode
                  ? "Template notifiche"
                  : intl.formatMessage(campaignMessages.newNotification)}
              </Title>
            </Box>
            <ImageManagerDialog />
          </Box>

          {/* FORM CREAZIONE */}
          <Notification
            position={-1}
            elem={creator}
            disabled={creator.enabled}
            handleChange={handleChange}
            name="CREATOR"
            title={isTemplateMode ? "Nuovo template" : "Nuova notifica"}
            values={values}
            status="init"
            handleSubmit={handleSubmit}
            handleSubmitWithDialog={handleSubmitWithDialog}
            consents={consents}
            imagesPath={imagesPath}
            notifConfig={notifConfig}
          />

          <h3>
            Riepilogo Notifiche:&nbsp;
            {report.total}&nbsp;(di cui attive&nbsp;{report.active})
          </h3>

          <Divider variant="middle" style={{ margin: "10px" }} />

          <div className="ui celled list">{renderedResults}</div>
        </Container>

        <FormDialog
          openModal={openModal}
          handleSubmit={handleSubmit}
          handleSubmitWithDialog={handleSubmitWithDialog}
        />
      </React.Fragment>
    )
  );
}
